// @flow

import React, { type Element, useCallback, useEffect, useState } from 'react'
import { TextField, Box, Typography } from '@material-ui/core'
import type { Facility } from '../../types'
import { Autocomplete } from '@material-ui/lab'
import { createFilterOptions } from '@material-ui/lab/Autocomplete'
import FacilityOption from './option'
import { grey } from '@material-ui/core/colors'

type Props = {|
  defaultFacility: Facility,
  setDefaultFacility: (Facility) => void,
  facilityList: Array<Facility>,
  facilitiesUpdated: boolean,
  handleServerErrors: ({ [string]: Array<string> }) => void,
  error: string,
|}

const createFilterOptionsFunc = createFilterOptions({
  limit: 5,
  stringify: (option) => option.code + ' ' + option.name,
})

function DefaultFacilitySelect({
  defaultFacility,
  setDefaultFacility,
  facilityList,
  facilitiesUpdated,
  handleServerErrors,
  error,
}: Props): Element<typeof React.Fragment> {
  const [facilities, setFacilities] = useState([])

  const updateFacilityFavorite = (id, isFavorite) => {
    const tempFacilities = facilities.map((facility) => {
      if (facility.id === id) {
        facility.favorite = isFavorite
      }
      return facility
    })
    setFacilities(tempFacilities)
  }

  const handleDefaultFacilityChange = (
    _event: SyntheticInputEvent<HTMLInputElement>,
    value: Facility,
    reason: string
  ): void => {
    if (reason !== 'select-option') return

    setDefaultFacility(value)
  }

  const filterOptions = (options, state) => {
    if (state.inputValue === '') {
      return options.filter((option) => {
        return option.favorite || option.private
      })
    } else {
      return createFilterOptionsFunc(options, state)
    }
  }

  const renderInput = (params) => (
    <TextField
      {...params}
      label="Default Facility"
      variant="outlined"
      error={error !== ''}
      helperText={error ? 'Facility ' + error : error}
    />
  )

  const renderOption = (option) => (
    <FacilityOption
      key={option.id}
      option={option}
      handleServerErrors={handleServerErrors}
      updateFacilityFavorite={updateFacilityFavorite}
    />
  )

  const renderGroup = (params) => [
    <Box pl={2} py={1} mb={1} key={params.key} borderBottom={1} color={grey[300]}>
      <Box color={grey[900]} fontWeight="fontWeightRegular">
        <Typography variant="body2">{params.group}</Typography>
      </Box>
    </Box>,
    params.children,
  ]

  const orderFacilitiesByGroup = useCallback(() => {
    return facilityList
      .map((option) => {
        let group = 'Others'
        if (option.private) {
          group = 'My Facilities'
        } else if (option.favorite) {
          group = 'Favourites'
        }
        return {
          group: group,
          ...option,
        }
      })
      .sort((a, b) => (b.private ? 1 : -1))
  }, [facilityList])

  useEffect(() => {
    const options = orderFacilitiesByGroup()
    setFacilities(options)
  }, [facilityList, orderFacilitiesByGroup, facilitiesUpdated])

  return (
    <Box py={2}>
      <Autocomplete
        id="default-facility-select"
        fullWidth
        options={facilities}
        filterOptions={filterOptions}
        groupBy={(option) => option.group}
        getOptionSelected={(option, value) => option.id === value.id}
        getOptionLabel={(option) => option.name}
        value={defaultFacility}
        onChange={handleDefaultFacilityChange}
        data-testid="default-facility-autocomplete"
        includeInputInList={false}
        renderInput={renderInput}
        renderOption={renderOption}
        renderGroup={renderGroup}
      />
    </Box>
  )
}

export default DefaultFacilitySelect
